import type { UpdateProfileBody } from "@nlc/shared-types";
import type { Control } from "react-hook-form";

import { Controller, useWatch } from "react-hook-form";

import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { ClearButton, Label, TextField } from "@/components/field";
import {
  CitySelect,
  CountrySelect,
  StateSelect,
  useLocationData,
} from "@/components/location-select";
import { useGetCustomerSuccessUsers } from "@/features/profile/hooks/useGetCustomerSuccessUsers";

interface ProfileOwnerAdminFieldsProps {
  control: Control<UpdateProfileBody>;
  isDisabledCustomerField?: boolean;
}

export function ProfileOwnerAdminFields({
  control,
  isDisabledCustomerField,
}: ProfileOwnerAdminFieldsProps) {
  const countryValue = useWatch({ control, name: "country" });
  const stateValue = useWatch({ control, name: "state" });

  const {
    cities,
    countries,
    handleCityChange,
    handleCountryChange,
    handleStateChange,
    hasCities,
    hasStates,
    isCountrySelected,
    isStateSelected,
    states,
  } = useLocationData({ countryValue, stateValue });
  const { data: customerSuccessUsers } = useGetCustomerSuccessUsers();

  return (
    <>
      <Controller
        control={control}
        name="customerSuccessId"
        render={({ field }) => (
          <TextField>
            <Label>Customer success</Label>
            <div className="relative">
              <ComboBox
                allowsEmptyCollection
                isDisabled={isDisabledCustomerField}
                menuTrigger="input"
                onSelectionChange={(key) =>
                  field.onChange(key ? Number(key) : null)
                }
                selectedKey={field.value ?? null}
              >
                <ComboBoxGroup>
                  <ComboBoxInput placeholder="Search..." />
                  <ComboBoxButton />
                </ComboBoxGroup>
                <ComboBoxPopover>
                  <ComboBoxListBox className="max-h-64">
                    {customerSuccessUsers?.map((u) => (
                      <ComboBoxListItem id={u.id} key={u.id}>
                        {u.name}
                      </ComboBoxListItem>
                    ))}
                  </ComboBoxListBox>
                </ComboBoxPopover>
              </ComboBox>
              {field.value != null && !isDisabledCustomerField && (
                <ClearButton
                  className="absolute right-8 top-1/2 -translate-y-1/2"
                  onPress={() => field.onChange(null)}
                />
              )}
            </div>
          </TextField>
        )}
      />

      <Controller
        control={control}
        name="country"
        render={({ field }) => (
          <CountrySelect
            onChange={(id, name) => {
              handleCountryChange(id);
              field.onChange(name);
            }}
            options={countries}
            value={field.value}
          />
        )}
      />

      <Controller
        control={control}
        name="state"
        render={({ field }) => (
          <StateSelect
            disabled={!isCountrySelected || !hasStates}
            onChange={(id, name) => {
              handleStateChange(id);
              field.onChange(name);
            }}
            options={states}
            value={field.value}
          />
        )}
      />

      <Controller
        control={control}
        name="city"
        render={({ field }) => (
          <CitySelect
            disabled={!isStateSelected || !hasCities}
            onChange={(id, name) => {
              handleCityChange(id);
              field.onChange(name);
            }}
            options={cities}
            value={field.value}
          />
        )}
      />
    </>
  );
}
